import React from 'react';
import MessageFeedback from './MessageFeedback';
import { Avatar } from '@mui/material';
import { ChatMessage } from '../types/chat';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PauseIcon from '@mui/icons-material/Pause';
import Patient from '../assets/Patient.png';
import Livy from '../assets/Livy.png';
import clsx from 'clsx';
import SourceButton from './SourceButton';
import RotatingTextLoader from './RotatingTextLoader';
import AudioLoader from './AudioLoader';
import AudioVisualizer from './AudioVisualizer';
import '../styles/MessageAudio.css';
import '../styles/AudioLoader.css';
import useI18n from '../hooks/useI18n';

interface MessageItemProps {
  message: ChatMessage;
  sessionId: string | null;
  audioEnabled?: boolean;
  currentlyPlayingId?: string | null;
  isApiLoading?: boolean;
  audioProgress?: number;
  onPlayAudio?: (messageId: string, text: string) => void;
  onStopAudio?: () => void;

  // isGlobalTyping?: boolean; // This prop is not needed here anymore, as the message itself will indicate typing via `isTypingPlaceholder`
}

const MessageItem: React.FC<MessageItemProps> = ({
  message,
  sessionId,
  audioEnabled = false,
  currentlyPlayingId = null,
  isApiLoading = false,
  audioProgress: _audioProgress,
  onPlayAudio,
  onStopAudio,
}) => {
  const t = useI18n();
  const isUser = message.role === 'user';
  const messageId = message.id || `msg-${Date.now()}`;

  // Check if this message is specifically the typing placeholder
  const isTypingPlaceholder = message.isTypingPlaceholder === true;

  // Define the loading states to rotate through with specific timings
  const loadingStates = [
    t.get('thinking'),
    t.get('searching'),
    t.get('analyzing'),
    t.get('processing'),
    t.get('typing'),
  ];

  // Custom timing for each message in milliseconds
  const messageTimings = [6000, 6000, 6500, 6500, 6500];

  const handlePlayAudio = () => {
    // Prevent playing audio for typing placeholder messages
    if (isTypingPlaceholder || !message.text) return;

    if (onPlayAudio && !isApiLoading) {
      onPlayAudio(messageId, message.text);
    } else {
      console.log(`MessageItem: Cannot play audio`, {
        hasOnPlayAudio: !!onPlayAudio,
        hasText: !!message.text,
        isLoading: isApiLoading,
        messageId,
        isTypingPlaceholder,
      });
    }
  };

  const handleStopAudio = () => {
    if (onStopAudio) {
      onStopAudio();
    }
  };

  const isMessagePlaying = currentlyPlayingId === messageId;

  const renderAudioControls = () => {
    // Do not show audio controls for user messages, or for typing placeholders
    if (!audioEnabled || isUser || isTypingPlaceholder) {
      // <-- Added isTypingPlaceholder here
      return null;
    }

    // Don't show for streaming messages (already handled by your existing logic)
    if (message.isStreaming) {
      return null;
    }

    // If THIS message is loading audio, show the loader
    if (isApiLoading) {
      return (
        <div className="message-audio-loading flex items-center">
          <AudioLoader
            isLoading={true}
            loadingText={t.get('audioGenerating')}
            size="small"
            variant="inline"
            showIcon={true}
            className="audio-loading-container"
          />
        </div>
      );
    }

    let buttonIcon;
    let buttonTooltip;

    if (isMessagePlaying) {
      buttonIcon = <PauseIcon fontSize="small" sx={{ color: '#848484' }} />;
      buttonTooltip = t.get('pauseAudio');
    } else {
      buttonIcon = <VolumeUpIcon fontSize="small" sx={{ color: '#848484' }} />;
      buttonTooltip = t.get('generatePlayAudio');
    }

    return (
      <div className="message-audio-controls flex items-center gap-2">
        <button
          onClick={isMessagePlaying ? handleStopAudio : handlePlayAudio}
          title={buttonTooltip}
          aria-label={buttonTooltip}
        >
          {buttonIcon}
        </button>

        {isMessagePlaying && (
          <AudioVisualizer
            isPlaying={true}
            variant="wave"
            size="small"
            color="#3f51b5"
            className="mx-1"
          />
        )}
      </div>
    );
  };

  const avatar = isUser ? (
    <Avatar src={Patient} className="w-8 h-8">
      <PersonIcon className="text-white w-5 h-5" />
    </Avatar>
  ) : (
    <Avatar src={Livy} className="w-8 h-8">
      <SmartToyIcon className="w-5 h-5" />
    </Avatar>
  );

  // Basic LLM Response Formatter
  const formatLLMResponse = text => {
    if (!text) return text;

    return (
      text
        // Convert markdown to HTML
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')

        // Format important callouts
        .replace(/^(IMPORTANT|NOTE|WARNING|TIP):\s*/gm, '<strong">$1:</strong> ')

        // Format numbered lists
        .replace(/^(\d+\.\s)/gm, '<strong">$1</strong>')

        // Convert line breaks
        .replace(/\n\n/g, '<br/><br/>')
        .replace(/\n/g, '<br/>')
    );
  };

  return (
    <div
      className={clsx(
        'w-full text-left mb-3 sm:mb-4 p-3 rounded-lg relative',
        isUser ? 'bg-gray-100' : 'bg-white',
        // Add min-height for typing placeholder to make centering more visible
        isTypingPlaceholder ? 'min-h-[80px]' : '',
        // Add subtle indication when loading audio, or if it's a typing placeholder
        (isApiLoading && !isUser) || isTypingPlaceholder ? ' ' : '' // <-- Modified for typing placeholder
      )}
    >
      <div
        className={clsx(
          'flex max-w-full gap-2',
          // Center vertically when it's a typing placeholder, otherwise align to start
          isTypingPlaceholder ? 'items-center min-h-[56px]' : 'items-start'
        )}
      >
        <div className="flex-shrink-0">{avatar}</div>

        <div
          className={clsx(
            'flex-grow max-w-full px-2 py-0',
            isTypingPlaceholder ? 'flex items-center' : 'self-center'
          )}
        >
          {/* Message Header with Audio Controls */}
          {!isUser && !isTypingPlaceholder && (
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500 font-medium">
                Livy
                {(message.isStreaming || isTypingPlaceholder) && (
                  <span className="ml-1 inline-flex items-center">
                    {/* <div className="text-xs text-gray-500 font-medium">is&nbsp;</div> */}
                    <RotatingTextLoader
                      messages={loadingStates}
                      timings={messageTimings}
                      defaultInterval={3000}
                      showDots={true}
                      className="text-blue-500 font-medium"
                    />
                  </span>
                )}
              </div>

              {/* Only show audio controls when not thinking/streaming */}
              {!(message.isStreaming || isTypingPlaceholder) && renderAudioControls()}
            </div>
          )}

          {/* Left-aligned thinking text for typing placeholder, but vertically centered */}
          {!isUser && isTypingPlaceholder && (
            <div>
              <div className="text-xs text-gray-500 font-medium">
                Livy
                <span className="ml-1 inline-flex items-center">
                  <RotatingTextLoader
                    messages={loadingStates}
                    timings={messageTimings}
                    defaultInterval={3000}
                    showDots={true}
                    className="text-blue-500 font-medium"
                  />
                </span>
              </div>
            </div>
          )}

          {/* Regular message content */}
          {!isTypingPlaceholder && (
            <div className="whitespace-pre-wrap break-words text-sm sm:text-base text-gray-800">
              <div className="markdown-body text-sm sm:text-base text-gray-800">
                <div
                  className="text-sm leading-snug text-gray-700"
                  dangerouslySetInnerHTML={{ __html: formatLLMResponse(message.text) }}
                />
              </div>

              {!isUser && <MessageFeedback message={message} sessionId={sessionId} />}
            </div>
          )}

          {/* Sources (AI only) - only for actual messages, not placeholders */}
          {message.role === 'ai' && message.sources?.length > 0 && !isTypingPlaceholder && (
            <SourceButton source={message.sources[0]} />
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageItem;
